import { useState } from 'react';

import ProgressArc from '../components/ProgressArc';
import SettingsModal from '../components/SettingsModal';
import { useDialog } from '../components/Dialog';
import { useStore } from '../store';
import type { Lesson } from '../types';

export default function UnitListScreen() {
  const s = useStore();
  const dialog = useDialog();
  const [showSettings, setShowSettings] = useState(false);

  const confirmDelete = async (lesson: Lesson) => {
    const name = lesson.unit.trim() || `Unit ${lesson.sid}`;
    const ok = await dialog.confirm(
      s.t('dialog.delete_unit_title'),
      s.t('dialog.delete_unit_body', { name, count: lesson.items.length }),
      {
        confirmLabel: s.t('dialog.delete'),
        cancelLabel: s.t('dialog.cancel'),
        destructive: true,
      },
    );
    if (ok) s.deleteUnit(lesson);
  };

  return (
    <div className="screen">
      <header className="appbar">
        <h1 className="brand">🎙&nbsp; Speak2Remember</h1>
        <button type="button" className="btn btn--primary btn--small" onClick={s.startAddSentences}>
          {s.t('nav.add_sentences_button')}
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setShowSettings(true)}
          aria-label={s.t('screen.settings.title')}
        >
          ⚙
        </button>
      </header>

      {s.lessons.length === 0 ? (
        <div className="center-fill">
          <div>
            <p className="empty-title">{s.t('unit_list.empty_title')}</p>
            <p className="empty-body">{s.t('unit_list.empty_body')}</p>
          </div>
        </div>
      ) : (
        <div className="scroll">
          <ul className="list">
            {s.lessons.map((lesson) => {
              const progress = s.unitProgress(lesson);
              const total = lesson.items.length;
              const name = lesson.unit.trim() || `Unit ${lesson.sid}`;
              return (
                <li key={lesson.sid}>
                  {/* The row is a button; the delete control sits beside it rather
                      than inside it, because nesting buttons is invalid HTML. */}
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.25rem' }}>
                    <button type="button" className="unit-card" onClick={() => s.openUnit(lesson)}>
                      <ProgressArc progress={progress} size={52} width={5}>
                        <span className="arc__pct-sm">{Math.round(progress * 100)}%</span>
                      </ProgressArc>
                      <span className="unit-card__mid">
                        <span className="unit-card__name">{name}</span>
                        {!!lesson.part.trim() && (
                          <span className="unit-card__part">{lesson.part.trim()}</span>
                        )}
                        <span className="unit-card__count">
                          {s.t('unit_row.sentence_count', { total })}
                        </span>
                      </span>
                      <span className="unit-card__total">
                        <span className="unit-card__total-num">{total}</span>
                        <span className="unit-card__total-suffix">{s.t('unit.count_suffix')}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => confirmDelete(lesson)}
                      aria-label={`${s.t('dialog.delete')}: ${name}`}
                    >
                      🗑
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <SettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}
